// use-orders.ts
// This file provides the useOrders hook for fetching and managing procedure orders with filtering, sorting, and pagination.
// It integrates with the Orders context and Athena/Cerner context for user and department information.

import { useQuery } from "@tanstack/react-query";
import { ProcedureResponse } from "@/types/my-tasks";
import { ProcArgs } from "@/types/procedure";
import { useOrdersContext } from "@/contexts/orders-context";
import { useAppState } from "@/contexts/app-state";
import { useQueryKeys } from "./use-query-keys";
import { ROWS_PER_PAGE } from "@components/Paginator";
import { getProcedures } from "@/services/procedures";

/**
 * useOrders Hook
 * Fetches and manages procedure orders for the current user, with support for filters, sorting, and pagination.
 * Integrates with Orders context and Athena/Cerner context for user and department information.
 *
 * @param user - The user for whom to fetch orders
 * @param filters - Filters to apply to the orders
 * @param bucketName - The bucket/category of orders
 * @param sortCriteria - The field to sort by
 * @param sortOrder - The order direction (asc/desc)
 * @returns The query object containing data, status, and helpers from React Query
 *
 * @example
 * const { data, isLoading } = useOrders({ user, filters, bucketName, sortCriteria, sortOrder });
 */
export const useOrders = ({
  user,
  filters,
  bucketName,
  sortCriteria,
  sortOrder,
}: Pick<
  ProcArgs,
  "user" | "filters" | "bucketName" | "sortCriteria" | "sortOrder"
>) => {
  const { pageNumber } = useOrdersContext();
  const { context } = useAppState();
  const getQueryKey = useQueryKeys("orders");

  const practiceId = context?.athena?.practiceId ?? "8363";
  const departmentId = context?.athena?.departmentId ?? "ABM-123";

  const sortMapper: { [key: string]: string, orderName: string } = {
    orderName: "procedureName",
    patientName: "patientName",
    dob: "dob",
    insurance: "carrier",
    orderingProvider: "providerName",
    performDate: "performDate",
    authStatus: "authStatus",
    scheduledStatus: "scheduledStatus",
    lastUpdated: "lastUpdated",
    facility: "facility",
  };

  return useQuery<ProcedureResponse, Error>({
    queryKey: getQueryKey({ user, filters, bucketName }),
    queryFn: () =>
      getProcedures({
        user,
        filters,
        pageNumber,
        pageSize: ROWS_PER_PAGE,
        practiceId,
        departmentId,
        bucketName,
        sortCriteria: sortMapper[sortCriteria as string],
        sortOrder,
      }),
    enabled: !!user,
  });
};
